/**
 * FlexiblePaymentEditor
 *
 * Editable list of custom payment rows (amount + due date).
 * Shown when the user selects "مرن / مخصص" (Custom) as the payment frequency.
 *
 * Responsibilities:
 *  - Add / remove payment rows
 *  - Show running sum vs target amount (both on the EX-VAT basis)
 *  - Per-row VAT breakdown (rent entered + conditional VAT + total)
 *  - Inline validation feedback (date out of range, sum mismatch)
 *
 * VAT model (locked decision): the user enters EX-VAT rent per row; the system
 * adds conditional VAT (0% residential / 15% commercial — `rentVatRate`). The
 * reconciliation (Payments total vs Target) is computed on the EX-VAT basis so
 * it matches the contract's full-term ex-VAT rent; the VAT-inclusive grand
 * total is shown separately. Mirrors the backend `update_contract_with_invoices`
 * regen path and the Review step's subtotal/VAT/total breakdown.
 */

import { RIYAL } from "@/lib/currency";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import {
  validatePaymentDatesInRange,
  validatePaymentSumEqualsTotal,
} from "@/lib/customPaymentValidation";
import { paymentVatBreakdown, scheduleVatTotals } from "@/lib/contractHelpers";
import type { PaymentScheduleItem } from "@/components/contracts/wizard-v2/types";

interface FlexiblePaymentEditorProps {
  payments: PaymentScheduleItem[];
  onChange: (payments: PaymentScheduleItem[]) => void;
  /** Full-term target on the EX-VAT basis (payments must reconcile to this). */
  targetTotal: number;
  startDate: string;
  endDate: string;
  /**
   * Conditional rent VAT rate: 0 for residential leases, 15 for commercial.
   * Derived upstream from the unit/property purpose. Defaults to 0 (exempt) so
   * the breakdown gracefully collapses to "no VAT" when the purpose is unknown.
   */
  rentVatRate?: number;
}

export function FlexiblePaymentEditor({
  payments,
  onChange,
  targetTotal,
  startDate,
  endDate,
  rentVatRate = 0,
}: FlexiblePaymentEditorProps) {
  const { t } = useLanguage();

  const outOfRangeIndices = new Set(
    validatePaymentDatesInRange(payments, startDate, endDate)
  );
  // Reconciliation is on the EX-VAT basis: targetTotal is full-term ex-VAT rent
  // and each row's amount is entered ex-VAT, so the sum check is apples-to-apples.
  const sumDiff = validatePaymentSumEqualsTotal(payments, targetTotal);
  const sumOk = Math.abs(sumDiff) <= 0.01;
  const vatApplies = rentVatRate > 0;
  const totals = scheduleVatTotals(
    payments.map((p) => Number(p.amount) || 0),
    rentVatRate
  );

  // ── Row helpers ──────────────────────────────────────────────────────────

  const addRow = () => {
    onChange([
      ...payments,
      {
        paymentNumber: payments.length + 1,
        dueDate: "",
        amount: 0,
      },
    ]);
  };

  const removeRow = (index: number) => {
    const next = payments
      .filter((_, i) => i !== index)
      .map((p, i) => ({ ...p, paymentNumber: i + 1 }));
    onChange(next);
  };

  const updateAmount = (index: number, value: string) => {
    const next = [...payments];
    next[index] = { ...next[index], amount: Number(value) || 0 };
    onChange(next);
  };

  const updateDate = (index: number, value: string) => {
    const next = [...payments];
    next[index] = { ...next[index], dueDate: value };
    onChange(next);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Reconciliation bar — Payments total vs Target on the EX-VAT basis. */}
      <div
        className={cn(
          "flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium",
          sumOk
            ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
            : "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
        )}
      >
        <span>
          {t("مجموع الدفعات (قبل الضريبة)", "Payments total (excl. VAT)")}:{" "}
          <strong>
            {formatNumber(totals.subtotal)}&nbsp;{RIYAL}
          </strong>
        </span>
        <span>
          {t("المستهدف (قبل الضريبة)", "Target (excl. VAT)")}:{" "}
          <strong>
            {formatNumber(targetTotal)}&nbsp;{RIYAL}
          </strong>
        </span>
        {!sumOk && payments.length > 0 && (
          <span className="flex items-center gap-1">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {sumDiff > 0
              ? t(
                  `زيادة ${formatNumber(Math.abs(sumDiff))}\u00A0${RIYAL}`,
                  `${formatNumber(Math.abs(sumDiff))}\u00A0${RIYAL} over`
                )
              : t(
                  `نقص ${formatNumber(Math.abs(sumDiff))}\u00A0${RIYAL}`,
                  `${formatNumber(Math.abs(sumDiff))}\u00A0${RIYAL} remaining`
                )}
          </span>
        )}
      </div>

      {/* VAT-inclusive grand total — shown separately so it never gets confused
          with the ex-VAT reconciliation above. */}
      {payments.length > 0 && (
        <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm space-y-1.5">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("إجمالي الإيجار (قبل الضريبة)", "Total Rent (excl. VAT)")}
            </span>
            <span className="font-medium">
              {formatNumber(totals.subtotal)}&nbsp;{RIYAL}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {vatApplies
                ? t(`ضريبة القيمة المضافة (${rentVatRate}%)`, `VAT (${rentVatRate}%)`)
                : t("ضريبة القيمة المضافة", "VAT")}
            </span>
            {vatApplies ? (
              <span className="font-medium">
                {formatNumber(totals.vat)}&nbsp;{RIYAL}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                {t("معفي من الضريبة", "VAT Exempt")}
              </span>
            )}
          </div>
          <div className="flex justify-between border-t pt-1.5 font-bold">
            <span>{t("الإجمالي الكلي (شامل الضريبة)", "Grand Total (incl. VAT)")}</span>
            <span className="text-primary">
              {formatNumber(totals.total)}&nbsp;{RIYAL}
            </span>
          </div>
        </div>
      )}

      {/* Payment rows */}
      {payments.length > 0 && (
        <div className="space-y-2">
          {/* Header row */}
          <div className="grid grid-cols-[2rem_1fr_1fr_2rem] gap-2 px-1 text-xs text-muted-foreground font-medium">
            <span className="text-center">#</span>
            <span>{t("الإيجار قبل الضريبة (⃁)", "Rent excl. VAT (⃁)")}</span>
            <span>{t("تاريخ الاستحقاق", "Due Date")}</span>
            <span />
          </div>

          {payments.map((payment, index) => {
            const dateError = outOfRangeIndices.has(index);
            const row = paymentVatBreakdown(Number(payment.amount) || 0, rentVatRate);
            return (
              <div
                key={index}
                className="grid grid-cols-[2rem_1fr_1fr_2rem] items-start gap-2"
              >
                {/* # */}
                <span className="flex h-9 items-center justify-center text-sm text-muted-foreground font-medium">
                  {payment.paymentNumber}
                </span>

                {/* Amount (entered ex-VAT) + per-row VAT breakdown */}
                <div className="space-y-1">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={payment.amount || ""}
                    onChange={(e) => updateAmount(index, e.target.value)}
                    placeholder="0"
                    className="text-end"
                    dir="ltr"
                  />
                  {row.subtotal > 0 && (
                    <p className="text-[11px] leading-tight text-muted-foreground">
                      {vatApplies
                        ? t(
                            `إيجار ${formatNumber(row.subtotal)} + ض.ق.م ${formatNumber(row.vat)} = ${formatNumber(row.total)}\u00A0${RIYAL}`,
                            `Rent ${formatNumber(row.subtotal)} + VAT ${formatNumber(row.vat)} = ${formatNumber(row.total)}\u00A0${RIYAL}`
                          )
                        : t(
                            `إيجار ${formatNumber(row.subtotal)}\u00A0${RIYAL} (معفي من الضريبة)`,
                            `Rent ${formatNumber(row.subtotal)}\u00A0${RIYAL} (VAT exempt)`
                          )}
                    </p>
                  )}
                </div>

                {/* Due date */}
                <div className="space-y-1">
                  <DatePicker
                    value={payment.dueDate}
                    onChange={(v) => updateDate(index, v)}
                    className={cn(dateError && "border-destructive")}
                  />
                  {dateError && (
                    <p className="text-xs text-destructive">
                      {t(
                        "خارج نطاق تواريخ العقد",
                        "Outside contract date range"
                      )}
                    </p>
                  )}
                </div>

                {/* Remove */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => removeRow(index)}
                  aria-label={t("حذف الدفعة", "Remove payment")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add row button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        {t("إضافة دفعة", "Add Payment")}
      </Button>
    </div>
  );
}
